import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { ProfileView } from "@/components/account/ProfileView";
import CarCard from "@/components/CarCard";
import { carModel, filterAndSortCars } from "@/lib/vehicle";
import { Seo } from "@/components/Seo";

export default function PublicProfile() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [profile, setProfile] = useState(undefined);
  const [make, setMake] = useState("All");
  const [model, setModel] = useState("All");
  const [location, setLocation] = useState("All");
  const [sort, setSort] = useState("price_asc");

  useEffect(() => {
    api.get(`/users/${id}/profile`).then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [id]);

  useEffect(() => { setModel("All"); }, [make]);

  const allCars = profile?.cars || [];
  const makes = useMemo(() => ["All", ...[...new Set(allCars.map((c) => c.make))].filter(Boolean).sort()], [allCars]);
  const models = useMemo(() => {
    const src = make === "All" ? allCars : allCars.filter((c) => c.make === make);
    return ["All", ...[...new Set(src.map(carModel))].filter(Boolean).sort()];
  }, [allCars, make]);
  const locations = useMemo(() => ["All", ...[...new Set(allCars.map((c) => c.pickup_location))].filter(Boolean).sort()], [allCars]);
  const cars = useMemo(() => filterAndSortCars(allCars, { make, model, location, sort }), [allCars, make, model, location, sort]);

  return (
    <div className="bg-white min-h-screen pt-28 pb-24 px-5 sm:px-8" data-testid="public-profile-page">
      <Seo noindex title={`${profile?.name || "Host Profile"} | Belo Rentals`} path={`/u/${id}`} />
      {profile === undefined ? (
        <p className="text-center text-slate-400">{t("common.loading")}</p>
      ) : profile === null ? (
        <p className="text-center text-slate-400" data-testid="profile-not-found">{t("publicProfile.notAvailable")}</p>
      ) : (
        <>
          <div className="max-w-3xl mx-auto">
            <ProfileView profile={profile} />
          </div>

          {profile.is_host && (
            <div className="max-w-6xl mx-auto mt-16 border-t border-slate-100 pt-12">
              <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                <h3 className="font-heading text-2xl font-bold text-slate-900 flex items-baseline gap-2.5" data-testid="host-vehicles-title">
                  {t("publicProfile.vehiclesTitle")}
                  <span data-testid="host-vehicle-count" className="text-sm font-semibold text-blue-600 bg-blue-50 rounded-full px-3 py-1">
                    {allCars.length} {allCars.length === 1 ? t("fleet.vehicle") : t("fleet.vehicles")}
                  </span>
                </h3>
                {allCars.length > 1 && (
                  <div data-testid="profile-filters" className="flex items-center gap-2 flex-wrap text-sm">
                    <select value={make} onChange={(e) => setMake(e.target.value)} data-testid="profile-make" className="belo-input py-2 cursor-pointer">
                      {makes.map((m) => <option key={m} value={m}>{m === "All" ? "All makes" : m}</option>)}
                    </select>
                    <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="profile-model" className="belo-input py-2 cursor-pointer">
                      {models.map((m) => <option key={m} value={m}>{m === "All" ? "All models" : m}</option>)}
                    </select>
                    <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="profile-location" className="belo-input py-2 cursor-pointer max-w-[220px]">
                      {locations.map((l) => <option key={l} value={l}>{l === "All" ? "All pickup locations" : l}</option>)}
                    </select>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="profile-sort" className="belo-input py-2 cursor-pointer">
                      <option value="price_asc">Price: low to high</option>
                      <option value="price_desc">Price: high to low</option>
                    </select>
                  </div>
                )}
              </div>
              {cars.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="host-vehicles-grid">
                  {cars.map((car, i) => (
                    <CarCard key={car.id} car={car} index={i} />
                  ))}
                </div>
              ) : (
                <p className="text-slate-400" data-testid="host-no-vehicles">{t("publicProfile.noVehicles")}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
